import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle, Car, MapPin, User, Phone, Mail } from "lucide-react";
import { fr } from "date-fns/locale";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Remplacement de pare-brise",
  "Réparation de vitrage (impact)",
  "Pose de films teintés",
  "Calibrage ADAS",
];

const timeSlots = [
  "08:00", "09:00", "10:00", "11:00",
  "14:00", "15:00", "16:00", "17:00", "18:00"
];

export default function AppointmentPage() {
  const [date, setDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    notes: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !selectedTime || !selectedService) {
      toast.error("Veuillez sélectionner une date, une heure et un service");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service: selectedService,
          date: format(date, "dd/MM/yyyy"),
          time: selectedTime,
          ...formData,
        }),
      });

      if (!response.ok) throw new Error("Erreur lors de la création du rendez-vous");

      setIsSuccess(true);
      toast.success("Votre rendez-vous a été enregistré avec succès !");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="pt-20 sm:pt-24 lg:pt-28 min-h-screen flex items-center justify-center" data-testid="appointment-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-12 rounded-3xl text-center max-w-lg mx-4"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Rendez-vous confirmé !
          </h2>
          <p className="text-slate-300 mb-8">
            Nous avons bien reçu votre demande de rendez-vous. 
            Vous recevrez une confirmation par email sous peu.
          </p>
          <div className="glass-card p-4 rounded-xl mb-8 text-left">
            <div className="space-y-2 text-sm">
              <p><span className="text-slate-500">Service :</span> <span className="font-medium text-white">{selectedService}</span></p>
              <p><span className="text-slate-500">Date :</span> <span className="font-medium text-white">{format(date, "dd MMMM yyyy", { locale: fr })}</span></p>
              <p><span className="text-slate-500">Heure :</span> <span className="font-medium text-white">{selectedTime}</span></p>
            </div>
          </div>
          <Button
            onClick={() => window.location.href = "/"}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8"
          >
            Retour à l'accueil
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="appointment-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              Réservation en ligne
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Prendre rendez-vous
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Choisissez votre créneau et nous interviendrons directement chez vous.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding">
        <div className="max-w-6xl mx-auto">
          <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-12">
            {/* Left - Calendar & Time */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              {/* Service Selection */}
              <div className="glass-card p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-5 h-5 text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">Service souhaité</h3>
                </div>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700" data-testid="service-select">
                    <SelectValue placeholder="Choisir un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Calendar */}
              <div className="glass-card p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <CalendarDays className="w-5 h-5 text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">Choisir une date</h3>
                </div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  locale={fr}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  className="rounded-xl border-0"
                  data-testid="appointment-calendar"
                />
              </div>

              {/* Time Slots */}
              <div className="glass-card p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">Choisir un créneau</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {timeSlots.map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setSelectedTime(time)}
                      className={`p-3 rounded-xl font-medium text-sm transition-colors ${
                        selectedTime === time
                          ? "bg-sky-500 text-white"
                          : "bg-slate-800/50 text-slate-200 hover:bg-sky-500/20"
                      }`}
                      data-testid={`time-slot-${time}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-8 rounded-3xl h-fit"
            >
              <h3 className="text-xl font-semibold text-white mb-6">
                Vos coordonnées
              </h3>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-sky-400" />
                    Nom complet
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-sky-400" />
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="06 00 00 00 00"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-sky-400" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jean.dupont@email.com"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="address" className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-sky-400" />
                    Adresse d'intervention
                  </Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 rue de la Paix, 57000 Metz"
                    required
                    className="bg-slate-800/50"
                    data-testid="input-address"
                  />
                </div>

                <div>
                  <Label htmlFor="notes" className="mb-2 block">
                    Notes supplémentaires (optionnel)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Informations complémentaires sur votre véhicule ou le lieu d'intervention..."
                    className="bg-slate-800/50 min-h-[100px]"
                    data-testid="input-notes"
                  />
                </div>

                {/* Summary */}
                {(date || selectedTime || selectedService) && (
                  <div className="bg-sky-50 p-4 rounded-xl border border-sky-100">
                    <h4 className="font-medium text-white mb-2">Récapitulatif</h4>
                    <div className="text-sm text-slate-300 space-y-1">
                      {selectedService && <p>Service : {selectedService}</p>}
                      {date && <p>Date : {format(date, "dd MMMM yyyy", { locale: fr })}</p>}
                      {selectedTime && <p>Heure : {selectedTime}</p>}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="submit-appointment"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner" />
                      Envoi en cours...
                    </span>
                  ) : (
                    "Confirmer le rendez-vous"
                  )}
                </Button>
              </div>
            </motion.div>
          </form>
        </div>
      </section>
    </div>
  );
}
